import type { Run } from '@/api/runs';
import type { ConfigValue, JsonValue } from '@/features/runs/ConfigForm';

export type RerunMode = 'failed' | 'review';

/** Where a re-run may start: a failed analysis from its failed step or an earlier one, a reviewed one from any step. */
export function rerunStartSteps(mode: RerunMode, stepCount: number, failedStep: number | null | undefined): number[] {
  const last = mode === 'failed' ? Math.min(failedStep ?? 0, stepCount - 1) : stepCount - 1;
  return Array.from({ length: Math.max(last + 1, 0) }, (_, i) => i);
}

/** The step's newest run, preferring one nothing superseded: what the re-run replaces. */
export function latestRunOfStep(runs: Run[], step: number): Run | undefined {
  const ofStep = runs.filter((r) => r.step_index === step).sort((a, b) => a.created_at.localeCompare(b.created_at));
  return ofStep.filter((r) => !r.superseded_by).at(-1) ?? ofStep.at(-1);
}

export interface ConfigChange {
  key: string;
  before: JsonValue | undefined;
  after: JsonValue | undefined;
}

/** Keys whose value differs between the two configs, in key order. */
export function configDiff(before: ConfigValue, after: ConfigValue): ConfigChange[] {
  const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])].sort();
  return keys.filter((k) => JSON.stringify(before[k]) !== JSON.stringify(after[k])).map((key) => ({ key, before: before[key], after: after[key] }));
}

/** A config value as one short line; `null` and a missing value read as unset. */
export function showValue(v: JsonValue | undefined, unset: string): string {
  if (v === undefined || v === null) return unset;
  return typeof v === 'string' ? v : JSON.stringify(v);
}

/** Staff run options from the advanced section; `resources` is the JSON text as typed. */
export interface AdvancedOptions {
  priority: '' | 'normal' | 'low';
  overflow: '' | 'allow' | 'pool';
  resources: string;
}

export const NO_ADVANCED: AdvancedOptions = { priority: '', overflow: '', resources: '' };

/**
 * The scheduling fields of the request; only what was set, so forun keeps its defaults otherwise.
 * Throws on resources that are not `{step: {knob: value}}`.
 */
export function advancedBody(o: AdvancedOptions): { priority?: 'normal' | 'low'; allow_overflow?: boolean; resources?: Record<string, Record<string, unknown>> } {
  const out: ReturnType<typeof advancedBody> = {};
  if (o.priority) out.priority = o.priority;
  if (o.overflow) out.allow_overflow = o.overflow === 'allow';
  if (o.resources.trim()) {
    const parsed: unknown = JSON.parse(o.resources);
    const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
    if (!isObject(parsed) || !Object.values(parsed).every((v) => isObject(v) && Object.keys(v).length > 0)) throw new Error('resources');
    if (Object.keys(parsed).length) out.resources = parsed as Record<string, Record<string, unknown>>;
  }
  return out;
}
